import { z } from 'zod';

export const apiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    message: z.string().optional(),
    data: dataSchema,
  });

export const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.boolean(),
    message: z.string().optional(),
    data: z.array(itemSchema),
    total: z.number().optional(),
    page: z.number().optional(),
    limit: z.number().optional(),
  });

export const errorResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  code: z.string().optional(),
  errors: z.record(z.any()).optional(),
});

export const oauthConfigSchema = z.object({
  tenantId: z.string().min(1),
  clientId: z.string().min(1),
  redirectUri: z.string().url(),
});

export const authUserSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
});

export const baseEntitySchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
});
// More lenient employee schema for nested use (e.g., in employeeWorkingModelSchema)
// This allows invalid emails since nested employee objects may have incomplete data
export const nestedEmployeeSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  fullname: z.string().optional(),
  email: z.string().optional(), // Accept any string, don't validate email format for nested objects
  role: z.string().optional(),
  status: z.string().optional(),
  location: z.string().optional(),
  time_model: z.string().optional(),
});
export const projectSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  project_number: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  manager: z.string().optional().nullable(),
  assistant: z.string().optional().nullable(),
  deputy: z.string().optional().nullable(),
  finance: z.string().optional().nullable(),
  status: z.string().optional(),
  portfolio: z.string().optional().nullable(),
  process_technology: z.string().optional().nullable(),
  delivery_model: z.string().optional().nullable(),
  start_date: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? '' : v)),
  end_date: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? '' : v)),
});

export const orderSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  order_number: z.string(),
  project_id: z.string(),
  project: projectSchema.optional(),
  comments: z.string().optional().nullable(),
  order_date: z.string().nullable(),
});

export const wbsSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  task_code: z.string(),
  project_type: z.string(),
  name: z.string().optional().nullable(),
  billable: z.boolean().optional(),
});
export const employeeProjectModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  employee: nestedEmployeeSchema.optional(),
  project_id: z.string(),
  project: projectSchema.optional(),
  sales_rate: z.number(),
  work_type: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string().optional(),
  order: z.string().optional(),
});
export const projectInfoSchema = z.object({
  project_id: z.string(),
  project_number: z.string(),
  project_name: z.string(),
});

export const orderInfoSchema = z.object({
  order_id: z.string(),
  order_number: z.string(),
  comments: z.string().optional(),
});

export const taskInfoSchema = z.object({
  task_id: z.string(),
  task_code: z.string(),
  task_name: z.string(),
  billable: z.boolean().optional(),
});

export const projectLogEntrySchema = z.object({
  id: z.string(),
  project_info: projectInfoSchema,
  order_info: orderInfoSchema.optional(),
  task_info: taskInfoSchema,
  hours_logged: z.number(),
  description: z.string().optional(),
  work_type: z.string().optional().nullable(),
  billable: z.boolean().optional(),
});

export const dayDetailSchema = z.object({
  id: z.string(),
  planned_hours: z.number(),
  start_time: z.string(),
  end_time: z.string(),
  pause: z.number(),
  project_log: z.array(projectLogEntrySchema).nullable(),
  status: z.string(),
});

export const timeEntryResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  'calendar-week': z.string(),
  data: z.object({
    days: z.record(z.string(), dayDetailSchema),
    status: z.string(),
  }),
});

export const employeeLoggedStatusSchema = z.object({
  employee_id: z.string(),
  date_of_year: z.string(),
  calendar_week: z.string(),
  status: z.string(),
  daily_hours_booked: z.number().optional(),
  total_project_hours_booked: z.number().optional(),
  is_holiday: z.boolean(),
  is_absence: z.boolean().optional(),
  is_vacation: z.boolean().optional(),
  is_bridge: z.boolean().optional(),
  has_planned_hours: z.boolean().optional(),
});

export const timeEntryStatusResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    day: z.array(employeeLoggedStatusSchema),
    week: z.record(z.string(), z.string()),
  }),
});

export const employeeProjectHoursSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  project_id: z.string(),
  order_id: z.string().optional(),
  task_id: z.string(),
  description: z.string().optional().nullable(),
  date_of_year: z.string(),
  hours_worked: z.number(),
  calendar_week: z.string(),
  status: z.string(),
  work_type: z.string().optional().nullable(),
  billable: z.boolean().optional(),
});

export const employeeLoggedHoursSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  date_of_year: z.string(),
  location_id: z.string(),
  working_time_model_id: z.string(),
  fte_hours: z.number(),
  start_time: z.string(),
  end_time: z.string(),
  pause_hours: z.number(),
  daily_hours_booked: z.number(),
  hours_adjusted: z.number(),
  total_project_hours_booked: z.number(),
  status: z.string(),
  calendar_week: z.string(),
});

export const favouriteSchema = z.object({
  id: z.string(),
  employee_id: z.string(),
  project_info: z.object({
    project_id: z.string(),
    project_number: z.string(),
    project_name: z.string(),
  }),
  order_info: z.object({
    order_id: z.string(),
    order_number: z.string(),
    comments: z.string(),
  }),
  task_info: z.object({
    task_id: z.string(),
    task_code: z.string(),
    task_name: z.string(),
    billable: z.boolean(),
  }),
  worktype: z.string().nullable().optional(),
  is_project_archived: z.boolean().optional(),
});

